import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import { admitLabel } from './AdmitProfilesPage';

interface Activity {
  id: string;
  title: string;
  position: string | null;
  description: string | null;
  category: string | null;
  hoursPerWeek: number | null;
  weeksPerYear: number | null;
  yearsParticipated: number | null;
  eliteActivityLabel: string | null;
}

interface AdmissionResult {
  id: string;
  universityName: string;
  status: string;
  category: string | null;
  isTopChoice: boolean;
  major: string | null;
  school: string | null;
  scholarshipAmount: number | null;
}

interface Award {
  id: string;
  title: string;
  level: string | null;
  description: string | null;
  category: string | null;
  year: number | null;
  eliteActivityLabel: string | null;
}

interface APIBScore {
  id: string;
  subject: string;
  score: string;
  scoreType: string;
  level: string | null;
  year: number | null;
  isTotal: boolean;
}

interface Essay {
  id: string;
  essayType: string;
  prompt: string;
  content: string;
  wordCount: number | null;
  universityName: string | null;
  ucQuestionNumber: number | null;
}

interface ProfileDetail {
  id: string;
  universityAttending: string | null;
  graduationYear: number | null;
  satScore: number | null;
  actScore: number | null;
  gpaWeighted: number | null;
  gpaUnweighted: number | null;
  gpaScale: number | null;
  major: string | null;
  totalApplications: number;
  totalAcceptances: number;
  totalRejections: number;
  totalWaitlisted: number;
  race: string[];
  gender: string | null;
  schoolType: string | null;
  country: string | null;
  state: string | null;
  isInternational: boolean | null;
  incomeLevel: string | null;
  firstGeneration: boolean | null;
  citizenshipStatus: string | null;
  classRank: number | null;
  classSize: number | null;
  percentile: number | null;
  recruitedAthlete: boolean | null;
  topChoiceUniversity: string | null;
  eliteActivities: string[];
  activities: Activity[];
  admissionResults: AdmissionResult[];
  awards: Award[];
  apibScores: APIBScore[];
  essays: Essay[];
}

const AdmitProfileDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading } = useUser();
  const [profile, setProfile] = useState<ProfileDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!user || !id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.get(`/admit-profiles/${id}`).then((data) => {
      if (cancelled) return;
      if (data?.success && data.profile) {
        setProfile(data.profile);
      } else {
        setError('Could not load this profile — it may have been removed.');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user, id]);

  const gpa =
    profile?.gpaUnweighted != null
      ? `GPA ${profile.gpaUnweighted}${profile.gpaScale ? ` / ${profile.gpaScale}` : ''}`
      : profile?.gpaWeighted != null
        ? `GPA ${profile.gpaWeighted}${profile.gpaScale ? ` / ${profile.gpaScale}` : ''} (weighted)`
        : null;

  const headerMeta = profile
    ? [
        profile.major || 'Undeclared',
        profile.schoolType,
        profile.state,
        profile.classRank != null
          ? `Rank ${profile.classRank}${profile.classSize ? ` of ${profile.classSize}` : ''}`
          : null
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <>
      {/* Header */}
      <header className="section" style={{ paddingBottom: 48 }}>
        <div className="wrap">
          <Link to="/resources/admit-profiles" className="meta" style={{ display: 'inline-block', marginBottom: 16 }}>
            ← All admit profiles
          </Link>
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            Admit profiles · anonymized
          </span>
          {profile && (
            <>
              <h1 style={{ maxWidth: '28ch' }}>
                {admitLabel(profile.universityAttending, profile.graduationYear)}
              </h1>
              {headerMeta && (
                <p className="meta" style={{ marginTop: 12 }}>
                  {headerMeta}
                </p>
              )}
            </>
          )}
        </div>
      </header>

      <section className="section warm" style={{ paddingTop: 48 }}>
        <div className="wrap" style={{ maxWidth: 880 }}>
          {isLoading && (
            <p className="meta" style={{ textAlign: 'center', padding: '40px 0' }}>
              Loading…
            </p>
          )}

          {!isLoading && !user && (
            <div className="card" style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
              <h3 style={{ marginBottom: 10 }}>Sign in to view this profile.</h3>
              <p className="meta" style={{ marginBottom: 18 }}>
                Full anonymized profiles — scores, activities, essays, and decisions — are free
                with any Common Room account.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link className="btn btn-primary" to="/login">
                  Sign in
                </Link>
                <Link className="btn btn-secondary" to="/family-signup">
                  Create a family account
                </Link>
              </div>
            </div>
          )}

          {!isLoading && user && loading && (
            <p className="meta" style={{ textAlign: 'center', padding: '40px 0' }}>
              Loading profile…
            </p>
          )}

          {!isLoading && user && !loading && error && (
            <div className="note" style={{ textAlign: 'center' }}>{error}</div>
          )}

          {!isLoading && user && !loading && !error && profile && (
            <>
              {/* Stats */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 32 }}>
                {profile.satScore != null && <span className="chip">SAT {profile.satScore}</span>}
                {profile.actScore != null && <span className="chip">ACT {profile.actScore}</span>}
                {gpa && <span className="chip">{gpa}</span>}
                {profile.firstGeneration && <span className="chip">First generation</span>}
                {profile.isInternational && <span className="chip">International</span>}
                {profile.recruitedAthlete && <span className="chip">Recruited athlete</span>}
                {profile.incomeLevel && <span className="chip">Income: {profile.incomeLevel}</span>}
                {profile.race.length > 0 && <span className="chip">{profile.race.join(', ')}</span>}
                {profile.gender && <span className="chip">{profile.gender}</span>}
              </div>

              {/* Admission results */}
              {profile.admissionResults.length > 0 && (
                <div style={{ marginBottom: 40 }}>
                  <h2 style={{ marginBottom: 16 }}>Admission decisions</h2>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {profile.admissionResults.map((r) => (
                      <div key={r.id} className="card">
                        <div
                          style={{
                            display: 'flex',
                            gap: 10,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            marginBottom: 6
                          }}
                        >
                          <h3 style={{ margin: 0 }}>{r.universityName}</h3>
                          <span className="chip">{r.status}</span>
                          {r.category && <span className="chip">{r.category}</span>}
                          {r.isTopChoice && <span className="chip">Top choice</span>}
                        </div>
                        <p className="meta">
                          {[
                            r.major,
                            r.school,
                            r.scholarshipAmount != null
                              ? `Scholarship: $${r.scholarshipAmount.toLocaleString()}`
                              : null
                          ]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activities */}
              {profile.activities.length > 0 && (
                <div style={{ marginBottom: 40 }}>
                  <h2 style={{ marginBottom: 16 }}>Activities</h2>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {profile.activities.map((a) => (
                      <div key={a.id} className="card">
                        <div
                          style={{
                            display: 'flex',
                            gap: 10,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            marginBottom: 6
                          }}
                        >
                          <h3 style={{ margin: 0 }}>{a.title}</h3>
                          {a.eliteActivityLabel && (
                            <span className="chip">{a.eliteActivityLabel}</span>
                          )}
                        </div>
                        <p className="meta" style={{ marginBottom: a.description ? 8 : 0 }}>
                          {[
                            a.position,
                            a.category,
                            [
                              a.hoursPerWeek != null ? `${a.hoursPerWeek} hrs/wk` : null,
                              a.weeksPerYear != null ? `${a.weeksPerYear} wks/yr` : null,
                              a.yearsParticipated != null ? `${a.yearsParticipated} yrs` : null
                            ]
                              .filter(Boolean)
                              .join(' · ') || null
                          ]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </p>
                        {a.description && (
                          <p className="meta" style={{ fontSize: 14.5 }}>{a.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Awards */}
              {profile.awards.length > 0 && (
                <div style={{ marginBottom: 40 }}>
                  <h2 style={{ marginBottom: 16 }}>Awards</h2>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {profile.awards.map((a) => (
                      <div key={a.id} className="card">
                        <div
                          style={{
                            display: 'flex',
                            gap: 10,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            marginBottom: 6
                          }}
                        >
                          <h3 style={{ margin: 0 }}>{a.title}</h3>
                          {a.eliteActivityLabel && (
                            <span className="chip">{a.eliteActivityLabel}</span>
                          )}
                        </div>
                        <p className="meta" style={{ marginBottom: a.description ? 8 : 0 }}>
                          {[a.level, a.year != null ? String(a.year) : null]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </p>
                        {a.description && (
                          <p className="meta" style={{ fontSize: 14.5 }}>{a.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AP / IB scores */}
              {profile.apibScores.length > 0 && (
                <div style={{ marginBottom: 40 }}>
                  <h2 style={{ marginBottom: 16 }}>AP / IB scores</h2>
                  <div className="card">
                    {profile.apibScores.map((s) => (
                      <p key={s.id} className="meta" style={{ marginBottom: 6 }}>
                        {s.subject} — {s.score} ({s.scoreType}
                        {s.level ? `, ${s.level}` : ''})
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Essays */}
              {profile.essays.length > 0 && (
                <div style={{ marginBottom: 40 }}>
                  <h2 style={{ marginBottom: 16 }}>Essays</h2>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {profile.essays.map((e) => (
                      <div key={e.id} className="card">
                        <details>
                          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                            {[
                              e.essayType,
                              e.universityName,
                              e.ucQuestionNumber != null ? `UC #${e.ucQuestionNumber}` : null,
                              e.wordCount != null ? `${e.wordCount} words` : null
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </summary>
                          <p className="meta" style={{ marginTop: 12, fontStyle: 'italic' }}>
                            {e.prompt}
                          </p>
                          <p style={{ marginTop: 12, whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                            {e.content}
                          </p>
                        </details>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Link to="/resources/admit-profiles" className="btn btn-secondary">
                ← Back to all admit profiles
              </Link>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default AdmitProfileDetailPage;
